import { Link, useLocation } from '@tanstack/react-router';
import { ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useShellSidebar } from '@/components/layout/shell-sidebar-context';
import { type ShellNavItem, useShellNav } from '@/components/layout/use-shell-nav';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/new-ui/tooltip';
import { cn } from '@/lib/utils';

type GroupId = 'tools';

// Which collapsible groups are open
const GROUPS_STORAGE_KEY = 'sidebar:groups';
const DEFAULT_GROUPS: Record<GroupId, boolean> = { tools: true };

function readGroups(): Record<GroupId, boolean> {
  try {
    const raw = localStorage.getItem(GROUPS_STORAGE_KEY);
    if (!raw) return DEFAULT_GROUPS;
    const saved = JSON.parse(raw) as Partial<Record<GroupId, boolean>>;
    return { ...DEFAULT_GROUPS, ...saved };
  } catch {
    return DEFAULT_GROUPS;
  }
}

// Fixed widths: full sidebar and the narrow icon rail. The rail keeps equal space left and right of the icons.
const RAIL_WIDTH = 64;
const EXPANDED_WIDTH = 216;

export const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  // On phones the saved desktop choice is ignored: the rail stays narrow unless opened this session
  const { collapsed, isMobile, setOpenMobile, toggleSidebar } = useShellSidebar();
  const { mainItems, toolItems } = useShellNav();
  const [groups, setGroups] = useState(readGroups);
  const showCollapsed = collapsed;
  const renderedWidth = collapsed ? RAIL_WIDTH : EXPANDED_WIDTH;

  // Nested routes (e.g. /estates/123) keep their section highlighted
  const isActive = (item: ShellNavItem) => {
    return (item.activePaths ?? [item.url]).some(
      (path) => currentPath === path || currentPath.startsWith(`${path}/`)
    );
  };

  const toggleGroup = (id: GroupId) => {
    setGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage unavailable: state still works for this session
      }
      return next;
    });
  };

  // Collapsed rail shows the name on hover; expanded rows show it inline
  const withTooltip = (label: string, node: JSX.Element) =>
    showCollapsed ? (
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={4}>
          {label}
        </TooltipContent>
      </Tooltip>
    ) : (
      node
    );

  const renderItem = (item: ShellNavItem) => {
    const active = isActive(item);
    return (
      <li key={item.url}>
        {withTooltip(
          item.title,
          <Link
            to={item.url}
            // Phones: the expanded menu covers most of the page, so close it after navigating
            onClick={() => isMobile && setOpenMobile(false)}
            aria-label={showCollapsed ? item.title : undefined}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'flex h-9 items-center gap-3 rounded-md px-[11px] text-sm transition-colors',
              'outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
              active
                ? 'bg-sidebar-accent font-medium text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/75 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground'
            )}
          >
            <item.icon className={cn('h-[18px] w-[18px] shrink-0', active && 'text-primary')} />
            <span
              className={cn(
                'truncate transition-opacity duration-150',
                showCollapsed && 'opacity-0'
              )}
            >
              {item.title}
            </span>
          </Link>
        )}
      </li>
    );
  };

  const renderGroup = (id: GroupId, label: string, items: ShellNavItem[]) => {
    // The rail has no group header to reopen a closed group, so it always shows the icons
    const isOpen = showCollapsed || groups[id];
    return (
      <div className="mt-4">
        {showCollapsed ? (
          <div className="mx-3 mb-2 h-px bg-sidebar-border" />
        ) : (
          <button
            type="button"
            onClick={() => toggleGroup(id)}
            aria-expanded={isOpen}
            className={cn(
              'mb-1 flex h-7 w-full items-center gap-1 rounded-md px-[11px] text-xs font-medium text-sidebar-foreground/55',
              'outline-none transition-colors hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring'
            )}
          >
            <span className="truncate">{label}</span>
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
                isOpen && 'rotate-90'
              )}
            />
          </button>
        )}
        {isOpen && <ul className="flex flex-col gap-0.5">{items.map(renderItem)}</ul>}
      </div>
    );
  };

  const toggleLabel = collapsed ? t('shellSidebarExpand') : t('shellSidebarCollapse');

  return (
    <div className="new-ui relative h-full shrink-0">
      <div
        data-state={collapsed ? 'collapsed' : 'expanded'}
        style={{ width: renderedWidth }}
        className={cn(
          'flex h-full flex-col bg-sidebar text-sidebar-foreground',
          'overflow-hidden border-r border-sidebar-border',
          'transition-[width] duration-200 ease-out'
        )}
      >
        <nav
          aria-label={t('shellSidebarNavLabel')}
          className="no-scrollbar ml-2 mr-4 flex flex-1 flex-col overflow-y-auto overflow-x-hidden pb-3 pt-2"
        >
          <ul className="flex flex-col gap-0.5">{mainItems.map(renderItem)}</ul>
          {toolItems.length > 0 && renderGroup('tools', t('tools'), toolItems)}
        </nav>
      </div>

      {/* Collapse / expand toggle sitting on the sidebar edge at the top */}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={toggleLabel}
            className={cn(
              'absolute right-0 top-3.5 z-30 flex h-6 w-6 translate-x-1/2 items-center justify-center rounded-md',
              'border border-sidebar-border bg-background text-sidebar-foreground/70 shadow-sm transition-colors',
              'outline-none hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring'
            )}
          >
            {collapsed ? (
              <ChevronsRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronsLeft className="h-3.5 w-3.5" />
            )}
          </button>
        </TooltipTrigger>
        {!isMobile && (
          <TooltipContent side="right" sideOffset={6}>
            {toggleLabel}
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
};
